(() => {
  const SELECTOR_ROOT = '[data-shade-finder]';

  function initShadeFinder(root) {
    if (!root || root.dataset.shadeFinderInitialized === 'true') return;
    root.dataset.shadeFinderInitialized = 'true';
    root.dataset.shadeFinderStep = root.dataset.shadeFinderStep || '1';

    const restrictedStateObserver = new MutationObserver(() => {
      syncRestrictedErrors(root);
    });
    restrictedStateObserver.observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-restricted-add-blocked']
    });

    root.addEventListener('click', event => {
      const productCard = event.target.closest('[data-shade-finder-product]');
      const shadeCard = event.target.closest('[data-shade-handle]');
      const backButton = event.target.closest('[data-shade-finder-back]');
      const submitter = event.target.closest(
        '.shade-finder__product-actions button[type="submit"], .shade-finder__product-actions input[type="submit"]'
      );

      if (submitter && root.contains(submitter)) {
        const actionsEl = submitter.closest('.shade-finder__product-actions');
        if (actionsEl && getBlockedForm(actionsEl)) {
          event.preventDefault();
          event.stopPropagation();
          showRestrictedError(actionsEl);
          return;
        }
      }

      if (productCard && root.contains(productCard)) {
        const productHandle =
          productCard.getAttribute('data-shade-finder-product') || '';
        root.dataset.shadeFinderSelectedProduct = productHandle;

        goToStep(root, 2);
        showShadesForProduct(root, productHandle);

        root.dispatchEvent(
          new CustomEvent('shadeFinder:productSelected', {
            detail: { productHandle },
            bubbles: true
          })
        );
        return;
      }

      if (shadeCard && root.contains(shadeCard)) {
        const shadeHandle = shadeCard.getAttribute('data-shade-handle') || '';
        const productHandle =
          shadeCard
            .closest('[data-shade-product]')
            ?.getAttribute('data-shade-product') ||
          root.dataset.shadeFinderSelectedProduct ||
          '';

        root.dataset.shadeFinderSelectedShade = shadeHandle;

        goToStep(root, 3);
        showVariantsForSelection(root, productHandle, shadeHandle);
        return;
      }

      if (backButton && root.contains(backButton)) {
        const currentStep = parseInt(root.dataset.shadeFinderStep || '1', 10);
        const previousStep = Math.max(1, currentStep - 1);
        goToStep(root, previousStep);
        return;
      }
    });

    updateProgress(root);
    updateStepPanels(root);
    syncRestrictedErrors(root);
  }

  function getRestrictedMessage() {
    const DEFAULT_MESSAGE =
      'Only Appointment, Certificate, or Gift products can be purchased in a single order. Please complete this purchase separately from other items.';
    return (
      window.themeVariables?.strings?.restrictedOrderErrorMessage ||
      DEFAULT_MESSAGE
    );
  }

  function getBlockedForm(actionsEl) {
    return actionsEl?.querySelector(
      'product-form[data-restricted-add-blocked="true"], form[data-restricted-add-blocked="true"]'
    );
  }

  function syncRestrictedErrors(root) {
    root
      .querySelectorAll('.shade-finder__product-actions')
      .forEach(actionsEl => {
        if (getBlockedForm(actionsEl)) {
          showRestrictedError(actionsEl);
        } else {
          removeRestrictedError(actionsEl);
        }
      });
  }

  function showRestrictedError(actionsEl) {
    if (!actionsEl) return;

    let banner = actionsEl.querySelector(
      '[data-shade-finder-restricted-error]'
    );
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'banner banner--error';
      banner.role = 'alert';
      banner.setAttribute('aria-live', 'polite');
      banner.dataset.shadeFinderRestrictedError = 'true';
      actionsEl.appendChild(banner);
    }

    banner.textContent = getRestrictedMessage();
  }

  function removeRestrictedError(actionsEl) {
    actionsEl?.querySelector('[data-shade-finder-restricted-error]')?.remove();
  }

  function updateProgress(root) {
    const currentStep = parseInt(root.dataset.shadeFinderStep || '1', 10);
    const steps = root.querySelectorAll('[data-step-index]');

    steps.forEach(stepEl => {
      const stepIndex = parseInt(
        stepEl.getAttribute('data-step-index') || '0',
        10
      );
      stepEl.classList.remove('is-active', 'is-complete');

      if (stepIndex < currentStep) {
        stepEl.classList.add('is-complete');
      } else if (stepIndex === currentStep) {
        stepEl.classList.add('is-active');
      }
    });
  }

  function updateStepPanels(root) {
    const currentStep = parseInt(root.dataset.shadeFinderStep || '1', 10);
    const panels = root.querySelectorAll('[data-shade-finder-step-panel]');
    const backButton = root.querySelector('[data-shade-finder-back]');
    const titleSteps = root.querySelectorAll(
      '[data-shade-finder-step-heading]'
    );

    panels.forEach(panel => {
      const stepIndex = parseInt(
        panel.getAttribute('data-shade-finder-step-panel') || '0',
        10
      );
      panel.hidden = stepIndex !== currentStep;
    });

    if (backButton) {
      backButton.hidden = currentStep <= 1;
    }

    titleSteps.forEach(titleStep => {
      const stepIndex = parseInt(
        titleStep.getAttribute('data-shade-finder-step-heading') || '0',
        10
      );
      titleStep.hidden = stepIndex !== currentStep;
    });
  }

  function goToStep(root, step) {
    root.dataset.shadeFinderStep = String(step);
    updateProgress(root);
    updateStepPanels(root);
  }

  function showShadesForProduct(root, productHandle) {
    const shadesPanel = root.querySelector(
      '[data-shade-finder-step-panel="2"]'
    );
    if (!shadesPanel) return;

    let foundGroup = false;

    shadesPanel.querySelectorAll('[data-shade-product]').forEach(group => {
      const matches =
        group.getAttribute('data-shade-product') === productHandle;
      group.hidden = !matches;
      if (matches) {
        foundGroup = true;
      }
    });

    const emptyFallback = shadesPanel.querySelector(
      '[data-shade-finder-shades-empty-global]'
    );
    if (emptyFallback) {
      emptyFallback.hidden = foundGroup;
    }
  }

  function showVariantsForSelection(root, productHandle, shadeHandle) {
    const variantsPanel = root.querySelector(
      '[data-shade-finder-step-panel="3"]'
    );
    if (!variantsPanel) return;

    let foundCount = 0;

    variantsPanel
      .querySelectorAll('[data-variant-product][data-variant-shade]')
      .forEach(group => {
        const matches =
          group.getAttribute('data-variant-product') === productHandle &&
          group.getAttribute('data-variant-shade') === shadeHandle;

        group.hidden = !matches;
        if (matches) {
          foundCount += 1;
        }
      });

    const variantsGrid = variantsPanel.querySelector(
      '.shade-finder__products-grid'
    );
    if (variantsGrid) {
      // Keep the same desktop column behavior rule:
      // < 3 => 4, exactly 3 => 3, 4+ => 4
      const columns = foundCount <= 2 ? 3 : foundCount === 3 ? 3 : 4;
      variantsGrid.style.setProperty('--shade-grid-columns', String(columns));
    }

    const emptyFallback = variantsPanel.querySelector(
      '[data-shade-finder-variants-empty-global]'
    );
    if (emptyFallback) {
      emptyFallback.hidden = foundCount > 0;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll(SELECTOR_ROOT).forEach(initShadeFinder);
    });
  } else {
    document.querySelectorAll(SELECTOR_ROOT).forEach(initShadeFinder);
  }
})();
